import React, { useEffect, useRef, useState } from 'react';
import {
    Box,
    Button,
    Container,
    createStyles,
    Grid,
    makeStyles,
    TextField,
    Theme
} from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import useCommonApi from '../../api/useCommonApi';
import { IEvent } from '../../interfaces/IEvent';
import ReferenciaNumberField from '../controls/ReferenciaNumberField';
import { useSnackbar } from 'notistack';
import { traducirButton, traducirLabel, traducirMessage } from '../../blocks/traductor';
import {
    IClienteReferenciaCatalogo,
    IGuiaClienteReferencia,
    mapClienteReferenciaCatalogo
} from '../../entities/guias/IGuiaClienteReferencia';

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            flexGrow: 1,
        },
        table: {
            paddingTop: '50px',
            minHeight: '220px'
        },
        tr: {
            fontWeight: 600
        },
        filters: {
            backgroundColor: '#ececec',
            boxShadow: 'none',
            verticalAlign: 'middle',
            padding: '10px'
        },
        label: {
            fontSize: '12px',
            fontWeight: 600,
            marginTop: '10px'
        },
        textField: {
            backgroundColor: '#fff'
        },
        icon: {
            width: '30px',
            height: '30px',
            minWidth: '30px',
            borderRadius: '15px',
            backgroundColor: '#19754e',
            color: '#fff',
            textAlign: 'right',
        },
        marginButton: {
            margin: '10px 0px 0 30px'
        }
    }),
);

interface ReferenciasGuiaProps {
    referencias: IGuiaClienteReferencia[];
    referenciasCatalogoCargado: boolean;
    clienteId: number;
    onCatalogoInicializado: (referencias: IGuiaClienteReferencia[]) => void;
    onAvanzar: (referencias: IGuiaClienteReferencia[]) => void;
}

const ReferenciasGuia = ({
    referencias: referenciasProp,
    referenciasCatalogoCargado,
    clienteId,
    onCatalogoInicializado,
    onAvanzar
}: ReferenciasGuiaProps) => {
    const classes = useStyles();
    const apiSource = useCommonApi();
    const { enqueueSnackbar } = useSnackbar();
    const catalogoFetchIniciado = useRef(false);

    const [referencias, setReferencias] = useState<IGuiaClienteReferencia[]>(referenciasProp ?? []);
    const [referenciaAdicional, setReferenciaAdicional] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (referenciasCatalogoCargado) {
            return;
        }
        if (catalogoFetchIniciado.current) {
            return;
        }
        catalogoFetchIniciado.current = true;
        buscarReferenciasCliente();
    }, [referenciasCatalogoCargado]);

    useEffect(() => {
        if (referenciasCatalogoCargado) {
            setReferencias(referenciasProp ?? []);
        }
    }, [referenciasCatalogoCargado, referenciasProp]);

    const buscarReferenciasCliente = () => {
        setLoading(true);
        getReferenciasCliente().then(dataCliente => {
            let refsMapeadas: IGuiaClienteReferencia[] = [];
            if (dataCliente != null && dataCliente.length) {
                refsMapeadas = mapClienteReferenciaCatalogo(dataCliente);
            }
            setReferencias(refsMapeadas);
            onCatalogoInicializado(refsMapeadas);
            setLoading(false);
        }).catch(() => {
            setReferencias([]);
            onCatalogoInicializado([]);
            setLoading(false);
        });
    };

    const getReferenciasCliente = async () => {
        return apiSource.obtenerAsync<IClienteReferenciaCatalogo[]>(
            'cliente-referencia/by-id/' + clienteId
        );
    };

    const nuevaReferencia = () => {
        const index = Math.max(...referencias.map(e => e.index), 0) + 1;
        const ref: IGuiaClienteReferencia = {
            clienteID: clienteId,
            clienteReferenciaID: 0,
            descripcion: traducirLabel(259),
            valor: referenciaAdicional,
            tipo: 'ALFANUMÉRICO',
            tipoNumerico: 'A25',
            longitud: 25,
            index
        };

        const actualizadas = [...referencias, ref];
        setReferencias(actualizadas);
        setReferenciaAdicional('');
    };

    const eliminarReferencia = (row: IGuiaClienteReferencia) => {
        const referenciasFilter = referencias.filter(ref => ref.index !== row.index);
        setReferencias(referenciasFilter);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | IEvent, row: IGuiaClienteReferencia) => {
        let referenciaValue = event.target.value as string;

        if (row.tipo === 'TEXTO' && referenciaValue !== '') {
            referenciaValue = referenciaValue.replace(/[^A-Za-z]/gi, '');
        }

        const copiaReferencia: IGuiaClienteReferencia = {
            ...row,
            valor: referenciaValue.toString()
        };

        setReferencias(referencias.map(m => (m.index !== row.index ? m : copiaReferencia)));
    };

    const handleSiguiente = () => {
        if (referencias.some(e => !e.valor)) {
            enqueueSnackbar(traducirMessage(102), { variant: 'error' });
            return;
        }

        onAvanzar(referencias);
    };

    return (<>
        <Container maxWidth="xl" className={classes.filters}>
            <Grid container>
                <Grid item xs={1}>
                    <div className={classes.label}>
                        {traducirLabel(258)}
                    </div>
                </Grid>

                <Grid item xs={8}>
                    {!loading &&
                        <form className={classes.root} noValidate autoComplete="off">
                            <TextField
                                id="guia-referencia-libre"
                                size="small"
                                className={classes.textField}
                                value={referenciaAdicional}
                                onChange={e => setReferenciaAdicional(e.target.value)}
                                inputProps={{
                                    maxLength: 25,
                                    pattern: '[a-z]{0,9}'
                                }}
                                variant="outlined"
                                fullWidth
                            />
                        </form>
                    }
                </Grid>
                <Grid item xs={2} className={classes.marginButton}>
                    <Button
                        className={classes.icon}
                        variant="contained"
                        color="primary"
                        disabled={!referenciaAdicional}
                        onClick={nuevaReferencia}
                    >
                        <Add />
                    </Button>
                </Grid>
            </Grid>
        </Container>

        <Container maxWidth="xl" className={classes.table}>
            <TableContainer>
                <Table aria-label="referencias-guia">
                    <TableHead>
                        <TableRow>
                            <TableCell className={classes.tr}>{traducirLabel(81)}</TableCell>
                            <TableCell className={classes.tr}>{traducirLabel(258)}</TableCell>
                            <TableCell className={classes.tr}>{traducirLabel(296)}</TableCell>
                            <TableCell className={classes.tr} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {!loading && referencias.map((row) => (
                            <TableRow key={row.index}>
                                <TableCell component="td" scope="row">
                                    {row.descripcion}
                                </TableCell>
                                <TableCell component="td" scope="row">
                                    {row.tipo === 'NUMÉRICO' &&
                                        <TextField
                                            size="small"
                                            value={row.valor ?? ''}
                                            required={row.clienteReferenciaID > 0}
                                            onChange={(e) => handleChange(e, row)}
                                            InputProps={{
                                                inputComponent: ReferenciaNumberField as any,
                                            }}
                                            inputProps={{
                                                maxLength: row.longitud > 0 ? row.longitud : 50,
                                            }}
                                            variant="outlined"
                                            fullWidth
                                        />
                                    }

                                    {row.tipo !== 'NUMÉRICO' &&
                                        <TextField
                                            size="small"
                                            key={row.index}
                                            value={row.valor ?? ''}
                                            onChange={(e) => handleChange(e, row)}
                                            inputProps={{
                                                maxLength: row.longitud > 0 ? row.longitud : 50,
                                                pattern: row.tipo === 'ALFANUMÉRICO' ? '[A-Za-z0-9]+' : '[A-Za-z]+'
                                            }}
                                            variant="outlined"
                                            fullWidth
                                        />
                                    }
                                </TableCell>
                                <TableCell component="td" scope="row">
                                    {row.tipoNumerico}
                                </TableCell>
                                <TableCell component="td" scope="row">
                                    <Button
                                        className={classes.icon}
                                        variant="contained"
                                        color="primary"
                                        onClick={() => eliminarReferencia(row)}
                                    >
                                        <Delete />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Container>

        <Container maxWidth="xl">
            <Box display="flex" justifyContent="flex-end" mt={2}>
                <Box>
                    <Button variant="contained" color="primary" onClick={handleSiguiente}>
                        {traducirButton(15)}
                    </Button>
                </Box>
            </Box>
        </Container>
    </>);
};

export default ReferenciasGuia;
